// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import api from './advisorApi';
import type { PageResponse } from '../types/relationship';
import type {
  ProfileRequestOverviewDTO,
  RequestOverviewDisplayStatus,
  RequestOverviewFilterOptionDTO,
  RequestOverviewQueryParams,
} from '../types/requestOverview';

/**
 * Client for the dashboard "Request Overview" endpoints
 * (`/api/requests/overview` + filter-option helpers). Tenant-scoped server-side
 * via the X-Brokerage-Id / X-Advisor-Id headers injected by the BFF.
 */
export const requestOverviewApi = {
  list: async (
    params: RequestOverviewQueryParams = {},
  ): Promise<PageResponse<ProfileRequestOverviewDTO>> => {
    const { data } = await api.get<PageResponse<ProfileRequestOverviewDTO>>(
      '/requests/overview',
      {
        params: {
          search: params.search || undefined,
          requestType: params.requestType?.length ? params.requestType : undefined,
          displayStatus: params.displayStatus?.length ? params.displayStatus : undefined,
          providerId: params.providerId?.length ? params.providerId : undefined,
          policyTypeId: params.policyTypeId?.length ? params.policyTypeId : undefined,
          viewAll: params.viewAll ?? undefined,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
        // Spring Cloud Gateway / RestTemplate accept repeated query params for
        // List<T>. Axios's default serialiser produces `requestType[]=` which
        // Spring does NOT bind — force `requestType=A&requestType=B`.
        paramsSerializer: { indexes: null },
      },
    );
    return data;
  },

  getProviderOptions: async (
    viewAll = false,
    search?: string,
    signal?: AbortSignal,
  ): Promise<RequestOverviewFilterOptionDTO[]> => {
    const { data } = await api.get<RequestOverviewFilterOptionDTO[]>(
      '/requests/overview/filter-options/providers',
      {
        params: {
          viewAll: viewAll || undefined,
          search: search?.trim() ? search.trim() : undefined,
        },
        signal,
      },
    );
    return data;
  },

  getPolicyTypeOptions: async (
    viewAll = false,
    search?: string,
    signal?: AbortSignal,
  ): Promise<RequestOverviewFilterOptionDTO[]> => {
    const { data } = await api.get<RequestOverviewFilterOptionDTO[]>(
      '/requests/overview/filter-options/policy-types',
      {
        params: {
          viewAll: viewAll || undefined,
          search: search?.trim() ? search.trim() : undefined,
        },
        signal,
      },
    );
    return data;
  },

  getRequestTypeOptions: async (
    viewAll = false,
    search?: string,
    signal?: AbortSignal,
  ): Promise<string[]> => {
    const { data } = await api.get<string[]>(
      '/requests/overview/filter-options/request-types',
      {
        params: {
          viewAll: viewAll || undefined,
          search: search?.trim() ? search.trim() : undefined,
        },
        signal,
      },
    );
    return data;
  },

  getDisplayStatusOptions: async (
    viewAll = false,
  ): Promise<RequestOverviewDisplayStatus[]> => {
    const { data } = await api.get<RequestOverviewDisplayStatus[]>(
      '/requests/overview/filter-options/display-statuses',
      { params: { viewAll: viewAll || undefined } },
    );
    return data;
  },
};

export default requestOverviewApi;
